import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import {
    Drawer, Box, Divider, List, ListItem,
    ListItemIcon, ListItemText, IconButton
} from "@mui/material";
import {
    SearchOutlined, MenuBook as MenuBookIcon
} from "@mui/icons-material";
import {
    User, BookOpen, Settings, LogOut,
    Home, Library, Star, MessageSquare,
    Moon, Sun, X, ChevronRight, Bell,
    Newspaper,
    MessageCircle,
    MessageCircleMore
} from "lucide-react";

import MenuAdmin from "../menuAdmin";

export default function Menu() {
    const { user, theme, toggleTheme, signout } = useAuth();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const [text, setText] = useState('');
    const [openDrawer, setOpenDrawer] = useState(false);

    // Função de Busca
    const handleSearch = () => {
        if (text.trim()) {
            navigate('/biblioteca', { state: text });
            setOpenDrawer(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };


    const handleSignOut = async () => {
        try {
            setOpenDrawer(false);
            await signout();
            navigate('/', { replace: true });
        } catch (error) {
            console.error("Erro ao sair:", error);
        }
    };

    useEffect(() => {
        const scrollTarget = [document.documentElement, document.body]; // html e body

        if (openDrawer ) {
            scrollTarget.forEach(el => el.style.setProperty('overflow', 'hidden', 'important'));
        } else {
            scrollTarget.forEach(el => el.style.removeProperty('overflow'));
        }

        return () => {
            scrollTarget.forEach(el => el.style.removeProperty('overflow'));
        };
    }, [openDrawer]);
    return (
        <div className={`${theme} sticky top-0 z-[100] w-full`}>
            {/* --- HEADER PRINCIPAL (Glassmorphism) --- */}
            <div className="w-full flex justify-center bg-blue2/90 backdrop-blur-md shadow-lg dark:bg-[#001e42]/90 border-b border-white/10 font-montserrat">
                <header className="w-full max-w-[1800px] px-6 py-3">
                    <nav className="flex justify-between items-center">

                        {/* Logo */}
                        <Link to="/dashboard" className="transition-transform hover:scale-105 active:scale-95">
                            <img src="../../assets/images/logoBranco.svg" alt="Logo" className="w-32" />
                        </Link>

                        {/* Navegação Central (Desktop) */}
                        <div className="hidden lg:flex items-center gap-8">
                            {[
                                { name: 'Home', path: '/dashboard' },
                                { name: 'Biblioteca', path: '/biblioteca' },
                                { name: 'Novidades', path: '/meus-livros' },
                                { name: 'Indicações', path: '/indicacoes' },
                                { name: 'Fórum', path: '/forum' }
                            ].map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`relative text-sm font-medium tracking-wide transition-colors hover:text-blue-200 text-white/90
                                    ${pathname == link.path ? 'text-white after:content-[""] after:absolute after:-bottom-2 after:left-0 after:w-full after:h-0.5 after:bg-blue-400 after:rounded-full' : ''}`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </div>

                        {/* Ações da Direita */}
                        <div className="flex items-center gap-3">
                            {/* Busca Minimalista */}
                            <div className="relative hidden md:flex items-center group">
                                <input
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    type="text"
                                    placeholder="Buscar título..."
                                    className="w-48 lg:w-64 h-9 pl-4 pr-10 rounded-full text-xs bg-white/10 border border-white/20 text-white placeholder-white/50 focus:bg-white focus:text-gray-900 focus:w-80 transition-all outline-none"
                                />
                                <button className="absolute right-3 text-white/50 group-focus-within:text-gray-500 transition-colors" onClick={handleSearch}>
                                    <SearchOutlined style={{ fontSize: '18px' }} />
                                </button>
                            </div>

                            {/* Tema */}
                            <IconButton
                                onClick={() => toggleTheme(theme === 'white' ? 'dark' : 'white')}
                                className="hover:bg-white/10 text-white"
                            >
                                {theme === 'white' ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} className="text-blue-200" />}
                            </IconButton>

                            {/* Trigger do Drawer (Foto do Usuário) */}
                            <button
                                onClick={() => setOpenDrawer(true)}
                                className="flex items-center gap-2 p-1 rounded-full border-2 border-transparent hover:border-white/40 transition-all active:scale-95"
                            >
                                <div className="w-9 h-9 rounded-full overflow-hidden border border-white/20 bg-slate-700">
                                    <img
                                        src={user?.img || "../../assets/images/default-user.png"}
                                        className="w-full h-full "
                                        alt="Avatar"
                                    />
                                </div>
                            </button>
                        </div>
                    </nav>
                </header>
            </div>

            {/* --- DRAWER LATERAL (Menu de Opções) --- */}
            <Drawer
                anchor="right"
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '85%', sm: '380px' },
                        backgroundColor: theme === 'dark' ? '#001e42' : '#ffffff',
                        backgroundImage: 'none',
                        borderLeft: '1px solid rgba(255,255,255,0.05)',
                        boxShadow: '-10px 0 30px rgba(0,0,0,0.1)',
                    }
                }}
            >
                <div className="absolute top-3 right-3 z-[110]">
                    <IconButton
                        onClick={() => setOpenDrawer(false)}
                        className="bg-gray-100/50 dark:bg-white/5 hover:bg-red-500 hover:text-white transition-all duration-300"
                        size="small"
                    >
                        <X size={20} className={` ${theme === 'dark' ? 'text-white/90' : 'text-gray-700'}`} />
                    </IconButton>
                </div>

                <Box className="flex flex-col h-full font-montserrat overflow-hidden">

                    {/* SEÇÃO DE PERFIL (Header do Drawer) */}
                    <div className={`p-6 pt-6 ${theme === 'dark' ? 'border-white/5 bg-black/10' : 'border-gray-100'}`}>
                        <div className="flex flex-col items-center text-center">
                            <div className="relative mb-2">
                                {/* Avatar com estilo "Card" */}
                                <img
                                    src={user?.img || "../../assets/images/default-user.png"}
                                    className="w-[70px] h-[70px] rounded-full shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500 border-2 border-white dark:border-white/5"
                                    alt="Avatar"
                                />

                            </div>

                            <h4 className={`font-black text-lg dark:text-white uppercase tracking-tighter ${theme === 'dark' ? 'text-white/90' : 'text-gray-700'}`}>
                                {user?.name}
                            </h4>
                            <div className="flex items-center gap-2 mt-1.5">
                                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue5 opacity-80">
                                    {user?.admin === 1 ? 'Administrador' : 'Leitor Prime'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <Divider className={`opacity-30 mx-10  ${theme === 'dark' ? 'border-white/5 ' : 'border-gray-100'}`} />

                    {/* LINKS DE NAVEGAÇÃO (Scrollable) */}
                    <List className="flex-1 px-6 py-6 space-y-1 overflow-y-auto custom-scrollbar">
                        <MenuOption

                            icon={<Home size={17} />}
                            label="Início / Dashboard"
                            active={pathname == '/dashboard'}
                            onClick={() => { navigate('/dashboard'); setOpenDrawer(false); }}

                        />
                        <MenuOption
                            icon={<Library size={17} />}
                            label="Explorar Acervo"
                            active={pathname == '/biblioteca'}
                            onClick={() => { navigate('/biblioteca'); setOpenDrawer(false); }}
                        />
                        <MenuOption
                            icon={<Newspaper size={17} />}
                            label="Novidades da Biblioteca"
                            active={pathname == '/meus-livros'}
                            onClick={() => { navigate('/meus-livros'); setOpenDrawer(false); }}
                        />
                        <MenuOption
                            icon={<Star size={17} />}
                            label="Minhas Indicações"
                            active={pathname == '/indicacoes'}
                            onClick={() => { navigate('/indicacoes'); setOpenDrawer(false); }}
                        />
                        <MenuOption
                            icon={<MessageCircleMore size={17} />}
                            label="Forum de Discussões"
                            active={pathname == '/forum'}
                            onClick={() => { navigate('/forum'); setOpenDrawer(false); }}
                        />

                        {/* SEÇÃO ADMIN INTEGRADA */}
                        {user?.admin === 1 && (
                            <div className={`border-t border-dashed ${theme === 'dark' ? 'border-white/10' : 'border-gray-100'}`}>

                                <MenuAdmin closeMenu={() => setOpenDrawer(false)} />
                            </div>
                        )}
                    </List>

                    {/* RODAPÉ DO DRAWER: LOGOUT */}
                    <div className={`p-4 border-t  ${theme === 'dark' ? 'border-white/5 bg-black/10' : 'border-gray-100'}`}>
                        <button
                            onClick={handleSignOut}
                            className={`w-full p-3 flex items-center justify-center gap-3 bg-white  text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/20 rounded-2xl font-bold text-xs uppercase tracking-[0.2em] transition-all active:scale-95 ${theme === 'dark' ? 'border-white/5 bg-white/5' : 'border-gray-100'}`}
                        >
                            <LogOut size={17} />
                            Encerrar Sessão
                        </button>
                    </div>
                </Box>
            </Drawer>
        </div>
    );
}

// Sub-componente para links do menu
function MenuOption({ icon, label, onClick, active }: any) {
    return (
        <ListItem
            button
            onClick={onClick}
            // Removi classes fixas de cor e deixei a lógica decidir tudo
            className={`rounded-2xl py-3 px-4 transition-all group flex items-center gap-3 relative z-10 ${active
                ? 'bg-blue-600/20 text-blue-500 '
                : 'hover:bg-white/5 text-slate-500'
                }`}
        >
            {/* Ícone: Usa text-current para herdar a cor do pai (ListItem) */}
            <div className={`flex items-center justify-center transition-colors ${active ? 'text-blue5' : 'group-hover:text-blue5 text-slate-500'
                }`}>
                {icon}
            </div>

            {/* Texto */}
            <ListItemText
                disableTypography
                primary={
                    <span className={`text-sm font-medium tracking-tight text-left flex-1 block transition-colors ${active
                        ? 'text-blue5'
                        : 'text-slate-500 group-hover:text-blue5'
                        }`}>
                        {label}
                    </span>
                }
            />

            {/* Seta indicadora (Chevron) */}
            <ChevronRight
                size={14}
                className={`transition-all duration-300 ${active
                    ? 'opacity-100 translate-x-0 text-blue5'
                    : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 text-slate-500'
                    }`}
            />
        </ListItem>
    );
}